import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');

// Dropdown shim — controlled by useState; closes on outside click.
const _DropdownCtx = React.createContext<any>({});
const BSDropdown: any = ({ align: _a, className, children, ...props }: any) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: any) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <_DropdownCtx.Provider value={{ open, setOpen }}>
      <div ref={ref} className={_cx('dropdown position-relative', className)} {...props}>
        {children}
      </div>
    </_DropdownCtx.Provider>
  );
};
BSDropdown.Toggle = ({
  as: _As,
  children,
  onClick,
  className,
  variant: _v,
  ...props
}: any) => {
  const { open, setOpen } = React.useContext(_DropdownCtx);
  const handle = (e: any) => {
    setOpen(!open);
    onClick?.(e);
  };
  return (
    <button
      type="button"
      className={_cx('btn btn-link p-0', className)}
      onClick={handle}
      {...props}
    >
      {children}
    </button>
  );
};
BSDropdown.Menu = ({ children, className, ...props }: any) => {
  const { open } = React.useContext(_DropdownCtx);
  if (!open) return null;
  return (
    <div
      className={_cx('dropdown-menu show', className)}
      style={{ position: 'absolute', right: 0, top: '100%', zIndex: 1000 }}
      {...props}
    >
      {children}
    </div>
  );
};
BSDropdown.Item = ({ children, onClick, className, ...props }: any) => {
  const { setOpen } = React.useContext(_DropdownCtx);
  const handle = (e: any) => {
    setOpen(false);
    onClick?.(e);
  };
  return (
    <button
      type="button"
      className={_cx('dropdown-item', className)}
      onClick={handle}
      {...props}
    >
      {children}
    </button>
  );
};
import {
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaMinus,
} from 'react-icons/fa';
import { LogOut, Palette, User as UserIcon, Wallet } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useLogoutMutation } from '@/features/auth/api';
import { isExitBlocked, getExitBlockReason } from '@/router/zustand/callStore';
import { useAuthStore } from '@/lib/auth/store';
import { clearLegacySessionKeys } from '@/lib/auth/strategy';
// USER_ROLES from utilities/constant matches the actual `role_*` values
// stored in useAuthStore.role (populated from v1's login payload). The
// alternate lib/constants/roles.ts uses `ROLE_*` values and doesn't
// define CALL_CENTRE_AGENT â€” every check against it silently fails.
import { ROUTES, USER_ROLES, USER_ROLES_MAP } from '@/utilities/constant';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeSettingsSheet } from '@/components/theme/ThemeSettingsSheet';
import AgentNotificationPopup from './AgentNotificationPopup';
import { fetchAssignedNotifications, playNotificationTone, watchNotifications } from '@/features/cti2/utils/dialerFormat';
import CreditsWalletPopover from './CreditsWalletPopover';
// Header renders the profile-type pill (`.agent-call-dropdown-*`) whose
// class set lives in the ported
// cti.css. Vite dedupes duplicate imports â€” CTIAgentUI also imports this.
import '@/features/cti/cti.css';
import { FaBell } from 'react-icons/fa';
import { getApiCall } from '@/service/httpCall';
import {
  API_CALL_CENTRE_CREDITS,
  API_COMPANY_WALLET_CREDITS,
} from '@/service/url';
import {
  getUser,
  hasSwitchBackStack,
  restoreActiveProfileFromSession,
  Storage,
  switchBackUser,
} from '@/utilities/setting';
import { BreakContext } from '@/router/context/BreakContext';
import {
  loginAsProfile,
  logoutAsProfile,
} from '@/features/cti/softphone/loginAsProfile';
import { ensureMicAllowed } from '@/features/cti/softphone/micPermission';
import { trackAppLogout } from '@/service/activityTracker';
import { useAclVersion } from '@/hooks/useSessionRefresh';

interface HeaderProps {
  username?: string;
  voiceCredits?: string | number;
  ttsCredits?: string | number;
  effectiveVoiceCredits?: string | number;
  effectiveTtsCredits?: string | number;
  aiAgentCredits?: string | number;
  effectiveAiAgentCredits?: string | number;
  totalResellers?: number;
  totalEndUsers?: number;
}

const ROLES_WITH_WALLET = [
  USER_ROLES.COMPANY_ADMIN,
  USER_ROLES.RESELLER,
  USER_ROLES.CALL_CENTRE_ADMIN,
];

// Ported byte-identically from v1 header.js:1228 â€” the same three types in
// the same order. "Soft Phone" is intentionally shown as Disabled in v1
// (the pill's disabled hint), same rule kept here.
const PROFILE_TYPE_OPTIONS = ['Soft Phone', 'Mobile', 'WEBRTC'] as const;

export function Header({
  username,
  voiceCredits,
  ttsCredits,
  effectiveVoiceCredits,
  effectiveTtsCredits,
  aiAgentCredits,
  effectiveAiAgentCredits,
  totalResellers,
  totalEndUsers,
}: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // useAuthStore.role holds the Java-backend role (e.g. `ROLE_SADMIN`) â€”
  // useful for admin gating on the shell but NOT what CTI code uses. The
  // v1-ported pages (nav-config filter, dashboard, CTI screens) all read
  // Storage.user.role which carries the v1 lowercase `role_*` values.
  // Read from Storage here so every role check in this file uses the
  // same source as the rest of the CTI subsystem.
  const [role, setRole] = useState<string | null>(() => getUser()?.role ?? null);
  const [email, setEmail] = useState<string | null>(() => getUser()?.email ?? null);
  const aclVersion = useAclVersion();
  useEffect(() => {
    // Storage.user is written at login / login-as and by the boot session
    // refresh. Re-read on route change so an impersonation swap picks up the
    // new identity without a full reload, and on `acl-refreshed` so a role
    // changed by the parent lands on the current page.
    setRole(getUser()?.role ?? null);
    setEmail(getUser()?.email ?? null);
  }, [location.pathname, aclVersion]);
  const hasStack = useAuthStore((s) => s.hasStack);
  const popSession = useAuthStore((s) => s.popSession);

  const logoutMutation = useLogoutMutation();

  const [themeOpen, setThemeOpen] = useState(false);

  // `isSAdmin` gates the Resellers / End Users totals (v1 header.js:1061).
  const isSAdmin = role === USER_ROLES.SUPERADMIN;

  const isAgent = role === USER_ROLES.CALL_CENTRE_AGENT;
  const roleLabel = (USER_ROLES_MAP as Record<string, string>)?.[role ?? ''] || null;
  const isOnDashboard = location.pathname === `/${ROUTES.DASHBOARD}`;

  // Break lifecycle (start/stop/countdown/exceeded) is fully owned by
  // BreakProvider â€” Header only reads state. Fall back to null-y defaults
  // when the provider isn't mounted (non-agent routes).
  const breakCtx: any = useContext(BreakContext) || {};
  const {
    userStatus = 'AVAILABLE',
    countdown = null,
    breakExceeded = false,
    activeBreak = null,
    dotColor = '#28a745',
  } = breakCtx;

  // Login-time seconds ticker, byte-identical to v1 header.js:272-318.
  // Stores an epoch-ms `loginTimestamp` in Storage on first mount so a
  // page refresh doesn't reset the clock, then ticks setLoginTime once
  // per second. `Storage.multiRemove(['loginTimestamp'])` in handleLogout
  // resets it on sign-out.
  const [loginTime, setLoginTime] = useState(0);
  const loginTsRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isAgent) return undefined;
    const now = Date.now();
    let loginTimestamp = Storage.getItem('loginTimestamp');
    if (!loginTimestamp) {
      loginTimestamp = String(now);
      Storage.setItem('loginTimestamp', loginTimestamp, true);
    }
    loginTsRef.current = parseInt(loginTimestamp, 10);
    const id = setInterval(() => {
      if (loginTsRef.current) {
        setLoginTime(Math.floor((Date.now() - loginTsRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(id);
  }, [isAgent]);

  const formatTime = (secs: number) => {
    const minutes = String(Math.floor(secs / 60)).padStart(2, '0');
    const seconds = String(secs % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  // Byte-identical to v1 header.js:359-370: the break capsule shifts from
  // green â†’ amber (â‰¤50% remaining) â†’ red (â‰¤25% remaining).
  const breakCapsuleColor = useMemo(() => {
    if (!activeBreak?.duration || countdown === null) return '#4bb567ff';
    const totalSeconds = Number(activeBreak.duration) * 60;
    if (totalSeconds === 0) return '#4bb567ff';
    const percentRemaining = (countdown / totalSeconds) * 100;
    if (percentRemaining <= 25) return '#f70303ff';
    if (percentRemaining <= 50) return 'rgb(190, 142, 0)';
    return '#4bb567ff';
  }, [activeBreak?.duration, countdown]);

  // Notifications state â€” the bell shows every role's assigned+broadcast
  // notifications. Fetched on mount, on socket notification_refresh /
  // (re)connect, on bell open, plus a 120s poll fallback.
  const [notifications, setNotifications] = useState<any[]>([]);
  const [hasUnread, setHasUnread] = useState(false);
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  const loadNotifications = async () => {
    if (!role) return;
    setIsNotificationLoading(true);
    try {
      const resp: any = await fetchAssignedNotifications();
      const raw = resp?.data || resp;
      const list = Array.isArray(raw) ? raw : [];
      const merged = list.filter((n: any) => !n.is_deleted);
      setNotifications(merged);
      setHasUnread(merged.some((n: any) => !n.is_read));
    } catch {
      /* noop */
    } finally {
      setIsNotificationLoading(false);
    }
  };

  useEffect(() => {
    if (!role) return undefined;
    loadNotifications();
    return watchNotifications(() => loadNotifications());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const prevUnreadRef = useRef<number | null>(null);
  useEffect(() => {
    const prev = prevUnreadRef.current;
    prevUnreadRef.current = unreadCount;
    if (prev !== null && unreadCount > prev) playNotificationTone();
  }, [unreadCount]);

  // Credits wallet state â€” v1 header.js:374-408. Company/Reseller hit
  // /companies/credits; CallCentreAdmin hits /call-centres/credits. Both
  // update three fields: voiceCredits (available), distributableCredits
  // (effective â€” what can be handed downstream), and ttsCredits.
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletVoiceCredits, setWalletVoiceCredits] = useState<any>(voiceCredits ?? '0');
  const [walletTtsCredits, setWalletTtsCredits] = useState<any>(ttsCredits ?? '0');
  const [walletDistributable, setWalletDistributable] = useState<any>(
    effectiveVoiceCredits ?? voiceCredits ?? '0'
  );
  const [walletTtsDistributable, setWalletTtsDistributable] = useState<any>(
    effectiveTtsCredits ?? ttsCredits ?? '0'
  );
  const [walletAiAgentCredits, setWalletAiAgentCredits] = useState<any>(
    aiAgentCredits ?? '0'
  );
  const [walletAiAgentDistributable, setWalletAiAgentDistributable] = useState<any>(
    effectiveAiAgentCredits ?? aiAgentCredits ?? '0'
  );
  const [isRefreshingCredits, setIsRefreshingCredits] = useState(false);
  const walletAnchorRef = useRef<HTMLDivElement | null>(null);
  const showWalletBtn = role && ROLES_WITH_WALLET.includes(role);

  useEffect(() => {
    setWalletVoiceCredits(voiceCredits ?? '0');
  }, [voiceCredits]);
  useEffect(() => {
    setWalletTtsCredits(ttsCredits ?? '0');
  }, [ttsCredits]);
  useEffect(() => {
    if (effectiveTtsCredits !== undefined) {
      setWalletTtsDistributable(effectiveTtsCredits);
    } else if (ttsCredits !== undefined) {
      setWalletTtsDistributable(ttsCredits);
    }
  }, [effectiveTtsCredits, ttsCredits]);
  useEffect(() => {
    if (effectiveVoiceCredits !== undefined) {
      setWalletDistributable(effectiveVoiceCredits);
    } else if (voiceCredits !== undefined) {
      setWalletDistributable(voiceCredits);
    }
  }, [effectiveVoiceCredits, voiceCredits]);
  useEffect(() => {
    setWalletAiAgentCredits(aiAgentCredits ?? '0');
  }, [aiAgentCredits]);
  useEffect(() => {
    if (effectiveAiAgentCredits !== undefined) {
      setWalletAiAgentDistributable(effectiveAiAgentCredits);
    } else if (aiAgentCredits !== undefined) {
      setWalletAiAgentDistributable(aiAgentCredits);
    }
  }, [effectiveAiAgentCredits, aiAgentCredits]);

  const refreshCredits = async () => {
    if (isRefreshingCredits) return;
    setIsRefreshingCredits(true);
    try {
      if (role === USER_ROLES.CALL_CENTRE_ADMIN) {
        const resp: any = await getApiCall(API_CALL_CENTRE_CREDITS, {});
        setWalletVoiceCredits(resp?.voice_credits ?? '0');
        setWalletDistributable(resp?.voice_credits ?? '0');
        setWalletTtsCredits(resp?.tts_credits ?? '0');
        setWalletTtsDistributable(
          resp?.tts_credits_distributable ?? resp?.tts_credits ?? '0'
        );
        setWalletAiAgentCredits(resp?.ai_agent_credits ?? '0');
        setWalletAiAgentDistributable(
          resp?.ai_agent_credits_distributable ?? resp?.ai_agent_credits ?? '0'
        );
      } else if (role === USER_ROLES.COMPANY_ADMIN || role === USER_ROLES.RESELLER) {
        const resp: any = await getApiCall(API_COMPANY_WALLET_CREDITS, {});
        setWalletVoiceCredits(resp?.credits_available ?? '0');
        setWalletDistributable(
          resp?.credits_distributable ?? resp?.credits_available ?? '0'
        );
        setWalletTtsCredits(resp?.tts_credits ?? '0');
        setWalletTtsDistributable(
          resp?.tts_credits_distributable ?? resp?.tts_credits ?? '0'
        );
        setWalletAiAgentCredits(resp?.ai_agent_credits ?? '0');
        setWalletAiAgentDistributable(
          resp?.ai_agent_credits_distributable ?? resp?.ai_agent_credits ?? '0'
        );
      }
    } catch {
      /* toast omitted — matches v1 which swallows the error */
    } finally {
      setIsRefreshingCredits(false);
    }
  };

  useEffect(() => {
    if (!role || !ROLES_WITH_WALLET.includes(role)) return undefined;
    void refreshCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);
  
  useEffect(() => {
    if (!walletOpen || !role || !ROLES_WITH_WALLET.includes(role)) return undefined;
    void refreshCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletOpen, role]);
  

  // Load the agent's userId + telephony profile types once on mount.
  // v1 does this inline via useEffect at header.js:465-470. `activeType`
  // is restored from sessionStorage so a refresh doesn't drop the pill.
  const [userId, setUserId] = useState<string | number | null>(null);
  const [types, setTypes] = useState<string[]>([]);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!isAgent) return;
    try {
      // Cross-tab activeLoginType sync: fall back into user Storage if the
      // session was resumed. Byte-identical to v1 header.js init.
      restoreActiveProfileFromSession();
    } catch {
      /* noop */
    }

    const user = getUser();
    if (!user) return;

    setUserId(user?.id ?? null);

    const profile: any[] = user?.agentData?.agentTelephony?.profile || [];
    if (Array.isArray(profile) && profile.length) {
      setTypes(profile.map((p) => p.type));
      const activeProfile = profile.find((p) => p.active_profile === true);
      setActiveType(activeProfile?.type || null);
    }
  }, [isAgent]);

  // Watch activeLoginType across tabs + same-tab events. v1 dispatches a
  // custom `sessionStorageChange` event; loginAsProfile / logoutAsProfile
  // both fire it. Also handle native `storage` events (other tab).
  useEffect(() => {
    if (!isAgent) return undefined;
    const sync = () => {
      const t = sessionStorage.getItem('activeLoginType');
      setActiveType(t || null);
    };
    const onCustom = (e: any) => {
      if (e?.detail?.key === 'activeLoginType') setActiveType(e.detail.value || null);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('sessionStorageChange', onCustom as any);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('sessionStorageChange', onCustom as any);
    };
  }, [isAgent]);

  const showAgentControls = isAgent && isOnDashboard;

  // Two impersonation stacks coexist in v2:
  //   - v2 native  â†’ useAuthStore.hasStack / popSession()
  //                  (sessionStorage key `obd3:auth:stack`)
  //   - v1 legacy  â†’ sessionStorage.userStack, written by switchUser() in
  //                  utilities/setting.ts (called by every switch-account
  //                  icon: UserListPage / CompanyListPage / CallCentreListPage
  //                  / AgentsListPage). Byte-identical to v1.
  // Logout must check BOTH so an impersonating parent returns to their
  // own session instead of being kicked to /login. See "return to parent"
  // in v1 header.js:736-748.

  // Ported byte-identically from v1 header.js:664-692. When an agent is
  // logged in via WEBRTC/Soft Phone, tear down the SIP UA and release the
  // mic BEFORE the logout API runs â€” otherwise Asterisk sees a dangling
  // registration racing against the license cleanup, and the browser
  // keeps its "tab is using the microphone" indicator lit after logout.
  const isWebrtcActive = (): boolean => {
    try {
      const t = (sessionStorage.getItem('activeLoginType') || '').trim().toUpperCase();
      return t === 'WEBRTC' || t === 'SOFT PHONE';
    } catch {
      return false;
    }
  };

  const teardownWebrtcOnLogout = async () => {
    if (!isWebrtcActive()) return;
    try {
      // Force-load the softphone module in case it hasn't been imported
      // yet (loginAs may not have run this session â€” a resume can leave
      // agentTelephony.profile.active_profile=true without initDailer).
      // Byte-identical to v1: `require("../CallIcon/demo-1")` â€” v2 uses
      // dynamic import so we can await module init before clicking.
      await import('@/features/cti/softphone/demo-1');

      const disconnectBtns = document.getElementsByClassName('disconnect2');
      // Copy the collection: clicking each button mutates the DOM and can
      // invalidate the live HTMLCollection iterator.
      Array.from(disconnectBtns).forEach((btn: any) => btn.click?.());

      sessionStorage.setItem('showWebrtc', 'NO');
      sessionStorage.removeItem('webrtcPhoneStatus');
      try {
        const modals = JSON.parse(sessionStorage.getItem('activeModal') || '[]');
        const updated = Array.isArray(modals) ? modals.filter((m) => m !== 'WEB') : [];
        if (updated.length > 0) {
          sessionStorage.setItem('activeModal', JSON.stringify(updated));
        } else {
          sessionStorage.removeItem('activeModal');
        }
      } catch {
        sessionStorage.removeItem('activeModal');
      }
    } catch (e) {
      console.log('WEBRTC teardown on logout failed', e);
    }

    // Release any live mic tracks (softphone + waveform). Byte-identical
    // to v1 header.js:687-692.
    try {
      if (typeof window !== 'undefined' && typeof (window as any).__ctiReleaseAllMedia === 'function') {
        (window as any).__ctiReleaseAllMedia();
      }
    } catch { /* noop */ }
  };

  const handleLogout = async () => {
    // This is a THIRD logout entry point (AppShell's own Header, which
    // still wraps the /cti2 route underneath the Agent Portal's full-
    // screen overlay) alongside Topbar.tsx and UserProfilePage.tsx — both
    // of those already block logout mid-call/mid-disposition, but this one
    // didn't, so an agent could still reach a real logout from here while
    // on an active call.
    if (isExitBlocked()) {
      toast.error(getExitBlockReason() || 'Finish your current call/disposition before logging out.');
      return;
    }
    // Prefer the v2 native stack when it has entries (nothing writes to it
    // today, but the API exists and takes precedence for correctness).
    if (hasStack && popSession()) {
      queryClient.clear();
      navigate('/');
      return;
    }

    trackAppLogout({ profile_type: activeType || null, from: 'header' });

    const legacyStackHasEntries = hasSwitchBackStack();

    // v1 header.js:1568-1572: if a telephony profile is active, log out
    // from it first so the SIP UA disconnects with a real activeType in
    // scope. teardownWebrtcOnLogout below catches the WEBRTC-specific
    // side effects even if this branch never ran.
    if (activeType && userId) {
      try {
        setIsProcessing(true);
        await logoutAsProfile(userId, activeType);
      } catch {
        /* proceed with normal logout */
      } finally {
        setIsProcessing(false);
      }
    }

    // Tear down SIP UA before hitting logout â€” see teardownWebrtcOnLogout.
    await teardownWebrtcOnLogout();

    // Always call the logout API so the CHILD session is invalidated on
    // the backend â€” matches v1 header.js:708 ("Always call logout API").
    // Swallow errors so a stale/expired token can't strand the parent
    // on the child session.
    try {
      await logoutMutation.mutateAsync(undefined);
    } catch {
      /* proceed to switch-back / login navigation */
    }

    // Clear persisted active-login-type so the next login doesn't
    // resurrect a stale WEBRTC/Soft Phone profile (v1 header.js:715-719).
    try {
      sessionStorage.removeItem('activeLoginType');
      localStorage.removeItem('activeLoginType');
    } catch { /* noop */ }

    // Reset login/idle timestamps so the online-time chip restarts on
    // next login. Byte-identical to v1 header.js:730-733.
    try {
      Storage.multiRemove?.(['loginTimestamp', 'idleTimestamp']);
    } catch { /* noop */ }

    queryClient.clear();

    if (legacyStackHasEntries) {
      const restored = await switchBackUser();
      if (restored) {
        // Full reload so useAuthStore.hydrate() re-reads sessionStorage.
        // Zustand doesn't watch sessionStorage; a soft navigate() would
        // leave the store pinned to the child's role/username.
        window.location.href = `/${ROUTES.DASHBOARD}`;
        return;
      }
      clearLegacySessionKeys();
    }

    window.location.replace(`/${ROUTES.LOGIN}`);
  };

  // Toggle handler ported byte-identically from v1 header.js:1272-1291.
  // Toggling to a NEW type auto-logs out of the currently-active type
  // first so an agent can't be logged into two profiles at once.
  const handleTypeToggle = async (type: string) => {
    if (!userId) return;
    const isDisabled = type === 'Soft Phone';
    if (isDisabled || isProcessing) return;
    const isActive = activeType === type;
    try {
      setIsProcessing(true);
      if (isActive) {
        await logoutAsProfile(userId, type);
      } else {
        if (type === 'WEBRTC' && !(await ensureMicAllowed())) return;
        if (activeType && activeType !== type) {
          await logoutAsProfile(userId, activeType);
        }
        await loginAsProfile(userId, type);
      }
    } catch {
      /* toast already fired inside login/logoutAsProfile */
    } finally {
      setIsProcessing(false);
    }
  };

  const initials = (username ?? '?')
    .split(/\s+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  // v1 filters options to the profile types the agent actually has, then
  // preserves the "Soft Phone / Mobile / WEBRTC" order.
  const availableTypes = useMemo(
    () => PROFILE_TYPE_OPTIONS.filter((t) => types.includes(t)),
    [types]
  );

  return (
    <>
      <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 lg:px-6">
        {/* Agent workspace navigation. v1 mounts this on the left, right
            after the logo/sidebar toggle. Only rendered on the dashboard
            route for the CALL_CENTRE_AGENT role. */}
        {/* Views dropdown removed for agents — the CTI's own workspace
         * tab strip in the NavBar (Call Stats / Voicemails / Leads /
         * Follow-Ups / Outgoing / Incoming) already surfaces every
         * report/view, making this dropdown a redundant second entry
         * point. Non-agent roles never see this branch anyway. */}

        <div className="ml-auto flex items-center gap-4">
          {isSAdmin && (
            <>
              {typeof totalResellers === 'number' && (
                <HeaderStat label="Resellers" value={totalResellers.toLocaleString()} />
              )}
              {typeof totalEndUsers === 'number' && (
                <HeaderStat label="End Users" value={totalEndUsers.toLocaleString()} />
              )}
            </>
          )}
          {/* Credits Wallet button + popover â€” v1 header.js:1061-1106.
              Visible only for Company/Reseller/CallCentreAdmin roles.
              Agents/SuperAdmin/SubSuperAdmin/TeamLead see nothing â€”
              byte-identical to v1 which never rendered credit chips
              for any other role. */}
          {showWalletBtn && (
            <div ref={walletAnchorRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setWalletOpen((v) => !v)}
                title="View credits wallet"
                style={{
                  background: walletOpen
                    ? 'rgba(255,255,255,0.22)'
                    : 'rgba(255,255,255,0.12)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  borderRadius: '12px',
                  padding: '6px 12px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: 'currentColor',
                  transition: 'all 0.2s ease',
                }}
              >
                <Wallet size={18} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>Credits</span>
              </button>
              <CreditsWalletPopover
                show={walletOpen}
                onHide={() => setWalletOpen(false)}
                containerRef={walletAnchorRef}
                voiceCredits={walletVoiceCredits}
                effectiveVoiceCredits={walletDistributable}
                hideEffectiveVoiceCredits={
                  getUser()?.assignedCompany?.is_own_company === true ||
                  role === USER_ROLES.CALL_CENTRE_ADMIN
                }
                ttsCredits={walletTtsCredits}
                effectiveTtsCredits={walletTtsDistributable}
                aiAgentCredits={walletAiAgentCredits}
                effectiveAiAgentCredits={walletAiAgentDistributable}
                onRefresh={refreshCredits}
                isRefreshing={isRefreshingCredits}
                onViewHistory={() => {
                  setWalletOpen(false);
                  navigate('/credit-history');
                }}
                onViewTtsHistory={() => {
                  setWalletOpen(false);
                  navigate('/tts-credits-logs');
                }}
                onViewAiAgentHistory={() => {
                  setWalletOpen(false);
                  navigate('/ai-agent-credits-logs');
                }}
              />
            </div>
          )}
          {hasStack && (
            <Badge variant="warning" className="hidden md:inline-flex">
              Impersonating
            </Badge>
          )}

          {/* Login/Logout profile pill â€” v1 header.js:1152-1332. Same order
              (Soft Phone / Mobile / WEBRTC), same active/disabled rules,
              same "Logout from: <ACTIVE>" toggle text. */}
          {showAgentControls && availableTypes.length > 0 && (
            <BSDropdown as="li" className="nav-item list-none">
              <BSDropdown.Toggle
                id="navbarDropdownCalls"
                variant="dark"
                className="agent-call-dropdown-toggle rounded-pill border-0 px-4 py-2"
                style={{
                  backgroundColor: 'rgb(112, 0, 164)',
                  border: '1px solid rgb(112, 0, 164)',
                  borderRadius: '40px',
                  padding: '3px 14px',
                  fontSize: '14px',
                  color: '#fffdf8f5',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                  cursor: 'pointer',
                }}
              >
                {activeType ? (
                  <>
                    Logout from:{' '}
                    <strong style={{ color: '#00ff80' }}>{activeType}</strong>
                  </>
                ) : (
                  'Login to take calls'
                )}
              </BSDropdown.Toggle>
              <BSDropdown.Menu
                className="dropdown-menu-end agent-call-dropdown-menu"
                style={{ height: 'auto', width: '200px' }}
              >
                {availableTypes.map((type) => {
                  const isActiveType = activeType === type;
                  const isDisabled = type === 'Soft Phone';
                  return (
                    <BSDropdown.Item
                      key={type}
                      as="div"
                      className="agent-call-dropdown-item"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        marginTop: '8px',
                        background: 'rgba(235, 235, 235, 0.03)',
                        transition: 'all 0.25s ease',
                      }}
                    >
                      <div
                        className="agent-call-dropdown-copy"
                        style={{ display: 'flex', flexDirection: 'column' }}
                      >
                        <span style={{ fontSize: '14px', fontWeight: 500 }}>
                          {type}
                        </span>
                        <span
                          style={{
                            fontSize: '11px',
                            marginTop: '2px',
                            fontWeight: 500,
                          }}
                        >
                          {isDisabled
                            ? 'Disabled'
                            : isActiveType
                              ? 'Active'
                              : 'Inactive'}
                        </span>
                      </div>
                      <div
                        onClick={() => handleTypeToggle(type)}
                        style={{
                          width: '44px',
                          height: '24px',
                          borderRadius: '50px',
                          background: isDisabled
                            ? '#3a3a3c'
                            : isActiveType
                              ? 'linear-gradient(135deg, #34c759, #30d158)'
                              : '#2c2c2e',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '3px',
                          cursor:
                            isDisabled || isProcessing
                              ? 'not-allowed'
                              : 'pointer',
                          opacity: isDisabled || isProcessing ? 0.5 : 1,
                        }}
                      >
                        <div
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#fff',
                            transform: `translateX(${isDisabled ? 0 : isActiveType ? 20 : 0}px)`,
                            transition: 'transform 0.2s',
                          }}
                        />
                      </div>
                    </BSDropdown.Item>
                  );
                })}
              </BSDropdown.Menu>
            </BSDropdown>
          )}

          {/* Break countdown + break-exceeded chips + Online chip â€”
              byte-identical to v1 header.js:1335-1444. Rendered for the
              CALL_CENTRE_AGENT role (Online is always visible; break
              chips only render while userStatus === 'BREAK'). */}
          {isAgent && countdown !== null && countdown > 0 && userStatus === 'BREAK' && (
            <div
              className="d-flex align-items-center gap-2"
              style={{
                backgroundColor: breakCapsuleColor,
                border: `1px solid ${breakCapsuleColor}`,
                borderRadius: '999px',
                padding: '6px 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#faf9f7ff',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                marginRight: '5px',
              }}
              title="Break Remaining"
            >
              <FaClock size={14} />
              <span>{activeBreak?.title || 'Break'}</span>
              {activeBreak?.duration && (
                <span
                  style={{
                    fontSize: '15px',
                    fontWeight: 700,
                    opacity: 1,
                    background: 'rgba(155, 127, 127, 0.2)',
                    borderRadius: '6px',
                    padding: '1px 6px',
                  }}
                >
                  {activeBreak.duration}m
                </span>
              )}
              <span
                style={{
                  fontWeight: 700,
                  minWidth: '50px',
                  display: 'inline-block',
                  textAlign: 'right',
                }}
              >
                {formatTime(countdown)}
              </span>
            </div>
          )}
          {isAgent && breakExceeded && userStatus === 'BREAK' && (
            <div
              className="d-flex align-items-center gap-2"
              style={{
                backgroundColor: '#fdecea',
                border: '1px solid #dc3545',
                borderRadius: '999px',
                padding: '6px 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#842029',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                cursor: 'pointer',
                marginRight: '5px',
              }}
              title="Break Time Exceeded"
            >
              <FaExclamationTriangle size={14} />
              <span>Break Exceeded</span>
            </div>
          )}
          {isAgent && (
            <div
              className="d-flex align-items-center gap-2"
              style={{
                backgroundColor: '#4bb567ff',
                border: '1px solid #4bb567ff',
                borderRadius: '999px',
                padding: '6px 14px',
                fontSize: '14px',
                fontWeight: 600,
                color: '#fffdf8f5',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                cursor: 'pointer',
              }}
              title="Online Time"
            >
              <FaClock size={14} />
              <span>Online</span>
              <span
                style={{
                  fontWeight: 700,
                  minWidth: '50px',
                  display: 'inline-block',
                  textAlign: 'right',
                }}
              >
                {formatTime(loginTime)}
              </span>
            </div>
          )}

          {/* Notification bell â€” v1 header.js:1108-1150. Visible for every
              logged-in role. Unread badge pulses on new items. Popup
              anchors underneath the bell and dismisses on outside click. */}
          {role && (
            <div
              style={{ position: 'relative', cursor: 'pointer' }}
              onClick={() => {
                setShowNotificationModal((prev) => !prev);
                if (!showNotificationModal) loadNotifications();
              }}
            >
              <FaBell size={18} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-10px',
                    backgroundColor: 'rgb(158, 0, 0)',
                    color: 'white',
                    borderRadius: '50%',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    minWidth: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {unreadCount}
                </span>
              )}
              <AgentNotificationPopup
                show={showNotificationModal}
                onHide={() => setShowNotificationModal(false)}
                setHasUnread={setHasUnread}
                isLoading={isNotificationLoading}
                notifications={notifications}
                setNotifications={setNotifications}
              />
              {/* hasUnread is exposed for tabs that want a subtle indicator */}
              <span style={{ display: 'none' }} data-has-unread={hasUnread ? '1' : '0'} />
            </div>
          )}

          {/* Appearance/theme toggle hidden for agents — they don't get
           * theme customization, and the icon just adds clutter to the
           * agent header. Superadmin / admin / team-lead still see it. */}
          {!isAgent && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setThemeOpen(true)}
              aria-label="Open appearance settings"
            >
              <Palette className="h-4 w-4" />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-10 gap-2.5 rounded-full border border-border/50 bg-card px-2 pr-3 shadow-sm hover:bg-accent"
              >
                {/* Avatar + optional status dot overlay for agents
                 * with a live telephony profile. dotColor comes from
                 * BreakContext (green available / amber break / red
                 * exceeded); breakExceeded swaps ✓ for − */}
                <div className="relative">
                  <Avatar className="h-8 w-8 ring-2 ring-background">
                    <AvatarFallback className="bg-primary text-xs font-bold text-primary-foreground">
                      {initials || '?'}
                    </AvatarFallback>
                  </Avatar>
                  {isAgent && activeType && (
                    <span
                      style={{
                        position: 'absolute',
                        bottom: '-1px',
                        right: '-1px',
                        width: '13px',
                        height: '13px',
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '2px solid hsl(var(--card))',
                      }}
                    >
                      {breakExceeded ? (
                        <FaMinus style={{ color: 'white', fontSize: '6px' }} />
                      ) : (
                        <FaCheck style={{ color: 'white', fontSize: '6px' }} />
                      )}
                    </span>
                  )}
                </div>
                <div
                  className="hidden min-w-0 flex-col text-left leading-tight md:flex"
                  title={roleLabel ?? undefined}
                >
                  <span className="truncate text-[13px] font-semibold text-foreground">
                    {username ?? 'Account'}
                  </span>
                  {email && (
                    <span
                      className="truncate text-[10.5px] font-medium text-muted-foreground"
                      title={email}
                    >
                      {email}
                    </span>
                  )}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-60 rounded-xl border border-border/60 bg-card p-1 shadow-xl"
            >
              <div className="flex items-center gap-3 rounded-lg px-3 py-2.5">
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-primary-foreground text-xs font-bold">
                    {initials || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {username ?? 'Account'}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">
                    Signed in
                  </div>
                </div>
              </div>
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                onClick={() => navigate('/profile')}
                className="cursor-pointer gap-2.5 rounded-md px-3 py-2 text-sm focus:bg-accent"
              >
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                Profile
              </DropdownMenuItem>
              {/* Appearance hidden for agents — they don't have theme
               * control at their level; admins/superadmins get it in
               * their own header. */}
              {!isAgent && (
                <DropdownMenuItem
                  onClick={() => setThemeOpen(true)}
                  className="cursor-pointer gap-2.5 rounded-md px-3 py-2 text-sm focus:bg-accent"
                >
                  <Palette className="h-4 w-4 text-muted-foreground" />
                  Appearance
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator className="my-1" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer gap-2.5 rounded-md px-3 py-2 text-sm text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <LogOut className="h-4 w-4" />
                {hasStack ? 'Switch back' : 'Logout'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      {/* Byte-identical to v1 header.js:1836-1838.
          The SIP softphone (features/cti/softphone/demo-1) binds its
          connect+register handler to every `.keypadConnect2` element
          and its disconnect handler to every `.disconnect2` element via
          `getElementsByClassName().forEach(el.addEventListener)` at
          initDailer() time. loginAsProfile / logoutAsProfile fire the
          click programmatically. Without these two hidden anchor
          elements in the DOM, the clickAll('keypadConnect2') no-ops
          and the SIP UA never registers â€” leaving the NavBar stuck on
          "Disconnected". */}
      <div style={{ display: 'none' }}>
        <button type="button" className="disconnect2" />
        <button type="button" className="keypadConnect2" />
      </div>
      <ThemeSettingsSheet open={themeOpen} onOpenChange={setThemeOpen} />
    </>
  );
}

function HeaderStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="hidden flex-col text-right leading-tight md:flex">
      <span className="text-2xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span className="text-sm font-semibold text-foreground">{value}</span>
    </div>
  );
}
